/**
 * THE TELEMETRY SEAM (APP-MONITORING M0.7, M1.5) — same shape as
 * apps/physio-app/src/monitoring/telemetry.ts, adapted for patient-web.
 *
 * Fire-and-forget by construction: the caller gets an event id back
 * synchronously and this function never awaits I/O, so it can be called from
 * a render-phase error boundary without risking a second failure.
 *
 * CONTEXT IS PRE-SCRUBBED BY SHAPE, NOT BY THIS FILE — callers pass only the
 * allow-listed context fields (mirrors the shared scrubber's `crash` sink).
 * There is no free-text field here on purpose (M1.4): patient-web has no
 * problem-report sink the way physio-app's K-PR5 does, so a caller that
 * wants to say more has nowhere safe to put it — don't add one here.
 */

import { uuidv7 } from '@advaitnetra/shared';

import { forwardToSentry } from './sentry';

/**
 * The allow-listed shape a crash event may carry (ADR-0024 decision 2,
 * mirrored from packages/shared/src/scrubber.ts's `crash` sink).
 * `phase` is patient-web's equivalent of physio-app's `inCall`: which of
 * App.tsx's Phase states the page was in, by NAME only — never a route
 * param, never the URL (which carries the link token).
 */
export interface TelemetryContext {
  screen?: string;
  phase?: string;
  release?: string;
  instance?: string;
  appRunId?: string;
  requestId?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface CaptureResult {
  /** A UUIDv7, mintable and showable before any SDK response exists. */
  eventId: string;
}

/**
 * Record an error. Logs locally (so a browser console still shows it),
 * mints an event id, and forwards to Sentry — a no-op until a DSN is
 * configured, and still synchronous.
 */
export function captureError(error: unknown, context: TelemetryContext = {}): CaptureResult {
  const eventId = uuidv7();
  try {
    // The one deliberate console write: there is no server-side log sink on
    // this bundle to fall back to.
    console.error('[telemetry]', error, { eventId, ...context });
    forwardToSentry(error, context, eventId);
  } catch {
    // Logging itself must never be the second failure in a crash path.
  }
  return { eventId };
}

/**
 * Record a non-fatal, operator-facing failure — distinguished from
 * `captureError` only by intent at the call site.
 */
export function captureMessage(message: string, context: TelemetryContext = {}): CaptureResult {
  return captureError(new Error(message), context);
}
